import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  Typography,
  Divider,
  IconButton,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  DialogContentText,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as PublishIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  Lightbulb as IdeaIcon,
  Replay as RetryIcon,
  VisibilityOutlined as PreviewIcon,
} from '@mui/icons-material';
import { PostPreview } from './PostPreview';
import { PlatformOutcomes, PlatformResult } from './PlatformOutcomes';
import { PostEngagement } from './PostEngagement';
import { PostComments } from './PostComments';
import { InstagramImageCheck } from './InstagramImageCheck';
import { getPlatformName, platformPostUrl } from '../config/platforms';
import { statusMeta, resultMeta, parseRejection } from '../config/postStatus';
import { useIsMobile } from '../hooks/useIsMobile';

export interface DetailPost {
  id: string;
  content: string;
  media_urls: string[];
  platforms: string[];
  status: string;
  scheduled_for?: string | null;
  published_at?: string | null;
  created_at: string;
  last_error?: string | null;
  results?: PlatformResult[];
  /** Per-platform caption overrides. Absent platforms use `content`. */
  content_variants?: Record<string, string> | null;
  /** Per-platform publish times. Absent platforms use `scheduled_for`. */
  schedule_variants?: Record<string, string> | null;
  /** Per-platform images, set by a retry with a replacement. Absent platforms use `media_urls`. */
  media_variants?: Record<string, string[]> | null;
  /** The next moment an unpublished platform is due, for a post going out in stages. */
  next_due_at?: string | null;
  /**
   * Set when the post has been soft-deleted. Actions are suppressed entirely in that case:
   * `status` survives a delete, so a deleted draft would otherwise still offer Edit and
   * Publish and appear to work.
   */
  deleted_at?: string | null;
}

interface Props {
  post: DetailPost | null;
  /** Where each platform would publish, from GET /api/platforms. */
  targets?: Record<string, string | null>;
  busy?: boolean;
  /** Actions are omitted entirely when the caller does not supply a handler. */
  onPublish?: (post: DetailPost) => void;
  onSchedule?: (post: DetailPost) => void;
  /** Clear the schedule, returning the post to Idea Staging with its content intact. */
  onStage?: (post: DetailPost) => void;
  onEdit?: (post: DetailPost) => void;
  onDelete?: (post: DetailPost) => void;
  /**
   * Open the per-network preview on its own. Offered only before a post has gone out — the
   * preview inside this dialog sits below outcomes and engagement that are empty until then,
   * which is how Post History users missed it (and why its cards got their own button).
   */
  onPreview?: (post: DetailPost) => void;
  /**
   * Send the post to one network again. `republish` is set for a network where it went out
   * and was then deleted on the platform by hand. `mediaUrls` replaces that network's images
   * for this attempt and after. Omitted, no retry controls are shown.
   */
  onRetry?: (post: DetailPost, platform: string, republish: boolean, mediaUrls?: string[]) => void;
  onClose: () => void;
}

/** The images one network gets: its own replacement if it has one, otherwise the post's. */
const mediaFor = (post: DetailPost, platform: string) =>
  post.media_variants?.[platform]?.length ? post.media_variants[platform] : post.media_urls;

/** Instagram refused the image itself, so a fitted copy is the fix rather than a plain retry. */
const isImageRefusal = (platform: string, code: string | null, message?: string) =>
  platform === 'instagram'
  && (['image_too_tall', 'image_too_wide', 'image_too_large'].includes(code || '') || /aspect ratio/i.test(message || ''));

const formatStamp = (stamp?: string | null) => {
  if (!stamp) return '';
  // SQLite CURRENT_TIMESTAMP is UTC with no zone marker; say so explicitly or the browser
  // reads it as local time and every timestamp is off by the offset.
  const iso = stamp.includes('T') ? stamp : stamp.replace(' ', 'T') + 'Z';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return stamp;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * The full text of a post, what happened on each platform, and the actions available.
 *
 * Shared by Post History and the Calendar. Which actions appear comes from the status
 * config rather than from each caller, so the two screens cannot disagree about whether a
 * partially-published post may be edited.
 */
export const PostDetailDialog: React.FC<Props> = ({
  post,
  targets = {},
  busy = false,
  onPublish,
  onSchedule,
  onStage,
  onEdit,
  onDelete,
  onPreview,
  onRetry,
  onClose,
}) => {
  const isMobile = useIsMobile();
  // The network awaiting a "did you really remove it?" answer before republishing
  const [confirmRepublish, setConfirmRepublish] = useState<string | null>(null);
  if (!post) return null;

  const meta = statusMeta(post.status);
  const notSent = (post.results || []).filter(r => r.status !== 'success');
  const isDeleted = Boolean(post.deleted_at);
  // Only a post with a time to give up. A staggered post that has already gone out on some
  // networks stays 'scheduled', but the server refuses to move it — so the button is hidden
  // rather than offered and refused.
  const canStage = meta.canSchedule
    && post.status !== 'draft'
    && !(post.results || []).some(r => r.status === 'success');

  // When the networks publish at different times there is no single date for this post.
  // `next_due_at` is the next one still to come, which is what "Due" should mean here —
  // scheduled_for would name a moment that has already been and gone for some networks.
  const times = post.platforms.map(p => ({
    platform: p,
    at: post.schedule_variants?.[p] || post.scheduled_for || null,
    result: post.results?.find(r => r.platform === p),
  }));
  const staggered = new Set(times.map(t => t.at)).size > 1;
  // A post moved back to Idea Staging keeps its times for when it is scheduled again. They are
  // listed so it is clear they survived, worded so nobody reads them as still due.
  const keptTimes = post.status === 'draft' && times.some(t => t.at);
  const stamp = post.published_at
    || (staggered ? post.next_due_at : null)
    // A staged draft keeps its old times, but is not due at them
    || (post.status !== 'draft' ? post.scheduled_for : null)
    || post.created_at;

  return (
    <Dialog fullScreen={isMobile} open onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Chip
            icon={meta.icon}
            label={meta.label}
            color={meta.color}
            size="small"
            variant={meta.color === 'default' ? 'outlined' : 'filled'}
          />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {post.status === 'scheduled' ? 'Due ' : ''}
            {formatStamp(stamp)}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Stated before the post itself, so the missing actions are explained rather than
            looking like a bug. Restoring happens in Post History -> Deleted posts, which is
            where the audit trail and the Restore button already live. */}
        {isDeleted && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This post was deleted{formatStamp(post.deleted_at!) ? ` on ${formatStamp(post.deleted_at!)}` : ''}.
            It is kept for 30 days and can be brought back from <strong>Deleted posts</strong> in
            Post History. Anything already published to a platform stays there.
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {meta.description}
        </Typography>

        {/* Per-platform outcomes, spelled out. The chips give the shape at a glance; this
            list gives the reason, which is the thing that was previously missing. */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Platforms</Typography>
        <PlatformOutcomes platforms={post.platforms} results={post.results} />

        {/* A post going out network by network needs its own schedule spelled out: the header
            can only name one moment, and the outcome chips say nothing about what is still
            ahead. Shown for every network, so "already published" and "due Thursday" sit in
            one list rather than having to be pieced together from two. */}
        {(staggered || keptTimes) && (
          <List dense sx={{ mt: 1, py: 0 }}>
            {times
              .slice()
              .sort((a, b) => String(a.at).localeCompare(String(b.at)))
              .map(t => (
                <ListItem key={t.platform} disableGutters sx={{ py: 0.25 }}>
                  <ListItemText
                    primary={
                      <Typography variant="caption" color="text.secondary">
                        <strong>{getPlatformName(t.platform)}</strong>
                        {t.result
                          ? t.result.status === 'success'
                            ? ` — published ${formatStamp(t.result.published_at) || ''}`.trimEnd()
                            : ` — ${t.result.status === 'rejected' ? 'not sent' : 'failed'}`
                          : t.at
                            ? ` — ${keptTimes ? 'kept for rescheduling:' : 'due'} ${formatStamp(t.at)}`
                            : ' — no time set'}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
          </List>
        )}

        {(post.results?.length ?? 0) > 0 && (
          <List dense sx={{ mt: 1 }}>
            {post.platforms.map(platform => {
              const result = post.results?.find(r => r.platform === platform);
              if (!result) return null;
              const rmeta = resultMeta(result.status);
              const url = platformPostUrl(platform, result.platform_post_id, result.permalink);
              const { text } = parseRejection(result.error_message);

              return (
                <ListItem key={platform} disableGutters sx={{ alignItems: 'flex-start' }}>
                  <ListItemIcon
                    sx={{
                      minWidth: 32,
                      mt: 0.5,
                      color:
                        result.status === 'success' ? 'success.main'
                          : result.status === 'rejected' ? 'warning.main'
                            : 'error.main',
                    }}
                  >
                    {rmeta?.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" component="span" fontWeight={500}>
                          {getPlatformName(platform)}
                        </Typography>
                        <Typography variant="body2" component="span" color="text.secondary">
                          — {result.status === 'rejected' ? 'not sent' : rmeta?.label.toLowerCase()}
                        </Typography>
                        {url && (
                          <Link
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5 }}
                          >
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </Link>
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        {text || null}
                        {/* Retrying is per network, so it sits on the network's own line. A
                            published network gets a quieter link rather than a button: the
                            only reason to send it again is that someone deleted it there. */}
                        {onRetry && !isDeleted && (
                          result.status === 'success' ? (
                            <Link
                              component="button"
                              variant="caption"
                              disabled={busy}
                              onClick={() => setConfirmRepublish(platform)}
                              sx={{ display: 'block', mt: 0.25 }}
                            >
                              Removed it from {getPlatformName(platform)}? Republish
                            </Link>
                          ) : (
                            <>
                              {/* The post is locked once it is out anywhere, so the image cannot
                                  be changed for everyone. A fitted copy goes to Instagram alone. */}
                              {isImageRefusal(platform, parseRejection(result.error_message).code, result.error_message) && (
                                <InstagramImageCheck
                                  urls={mediaFor(post, platform)}
                                  fixSuffix=" and retry"
                                  onReplace={(oldUrl, fitted) => onRetry(
                                    post,
                                    platform,
                                    false,
                                    mediaFor(post, platform).map(u => (u === oldUrl ? fitted.url : u)),
                                  )}
                                />
                              )}
                              <Button
                                size="small"
                                startIcon={<RetryIcon />}
                                disabled={busy}
                                onClick={() => onRetry(post, platform, false)}
                                sx={{ display: 'flex', mt: 0.5, ml: -0.5 }}
                              >
                                Retry {getPlatformName(platform)}
                              </Button>
                            </>
                          )
                        )}
                      </>
                    }
                    secondaryTypographyProps={{ variant: 'caption', component: 'div' }}
                  />
                </ListItem>
              );
            })}
          </List>
        )}

        {/* Engagement, per platform. Only rendered when something actually published — a post
            that reached nobody has nothing to measure, and an empty section would imply the
            figures are missing rather than inapplicable. */}
        {(post.results || []).some(r => r.status === 'success') && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Engagement</Typography>
            <PostEngagement results={post.results} variant="detail" />
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Comments</Typography>
            {/* key: a new post resets the loaded comments rather than showing the last one's */}
            <PostComments key={post.id} postId={post.id} />
          </>
        )}

        {/* A rejection is fixable, so say what to do about it rather than only what failed */}
        {notSent.some(r => r.status === 'rejected') && meta.canPublish && !isDeleted && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Nothing was sent to the platforms listed above. Fix the reason — shorten the post,
            connect the platform, or add media — then publish again.
          </Alert>
        )}

        {post.last_error && post.status === 'failed' && (
          <Alert severity="error" sx={{ mt: 1 }}>{post.last_error}</Alert>
        )}

        <Divider sx={{ my: 2.5 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Full text</Typography>
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.7, mb: 1 }}
        >
          {post.content}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {post.content.length.toLocaleString()} characters
        </Typography>

        <Divider sx={{ my: 2.5 }} />

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>How it appears per platform</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          An approximation for checking length and layout, not a pixel-accurate mock.
        </Typography>

        <PostPreview
          content={post.content}
          contentVariants={post.content_variants}
          platforms={post.platforms}
          mediaUrls={post.media_urls}
          mediaVariants={post.media_variants}
          results={post.results as never}
          targets={targets}
        />
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          {onDelete && meta.canDelete && !isDeleted && (
            <Button
              color="error"
              startIcon={<DeleteIcon />}
              disabled={busy}
              onClick={() => onDelete(post)}
            >
              Delete
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={onClose}>Close</Button>
          {onPreview && (meta.canEdit || meta.canPublish) && !isDeleted && (
            <Button startIcon={<PreviewIcon />} onClick={() => onPreview(post)}>
              Preview
            </Button>
          )}
          {onEdit && meta.canEdit && !isDeleted && (
            <Button startIcon={<EditIcon />} disabled={busy} onClick={() => onEdit(post)}>
              Edit
            </Button>
          )}
          {onStage && canStage && !isDeleted && (
            <Button startIcon={<IdeaIcon />} disabled={busy} onClick={() => onStage(post)}>
              Move to Idea Staging
            </Button>
          )}
          {onSchedule && meta.canSchedule && !isDeleted && (
            <Button startIcon={<ScheduleIcon />} disabled={busy} onClick={() => onSchedule(post)}>
              {post.status === 'scheduled' ? 'Reschedule' : 'Schedule'}
            </Button>
          )}
          {onPublish && meta.canPublish && !isDeleted && (
            <Button
              variant="contained"
              startIcon={<PublishIcon />}
              disabled={busy}
              onClick={() => onPublish(post)}
            >
              Publish now
            </Button>
          )}
        </Box>
      </DialogActions>

      <Dialog open={Boolean(confirmRepublish)} onClose={() => setConfirmRepublish(null)} maxWidth="xs">
        <DialogTitle>Republish to {confirmRepublish && getPlatformName(confirmRepublish)}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Only do this if the post is no longer on {confirmRepublish && getPlatformName(confirmRepublish)}
            {' '}— deleted there by hand, for example. If it is still up, it will appear twice.
            {confirmRepublish === 'facebook' || confirmRepublish === 'instagram'
              ? ' ShoutVector checks with the platform first and stops if it is still there.'
              : ' ShoutVector cannot check this network for you.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRepublish(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const platform = confirmRepublish!;
              setConfirmRepublish(null);
              onRetry?.(post, platform, true);
            }}
          >
            It's removed — republish
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
